import { useState, useCallback, useEffect } from 'react'
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useTransactionStatus } from '@/hooks'
import { APIWeb } from '@/api/index'
import PageLayout from '@/components/PageLayout'
import Header from '@/components/Header'
import Back from '@/components/Back'
import RefreshList from '@/components/RefreshList'
import Ripple from '@/components/Ripple'
import EmptyState from '@/components/EmptyState'
import SkeletonBox from '@/components/SkeletonBox'
import { formatDateTime, formatThousands } from '@/utils'
import { getTransactionIcon } from '../utils'

// 卡片状态映射
const CardStatusMap: Record<string, { text: string; color: string }> = {
  active: { text: '已激活', color: '#52C41A' },
  frozen: { text: '已冻结', color: '#FAAD14' },
  closed: { text: '已注销', color: '#FF4D4F' },
  pending: { text: '开卡中', color: '#1890FF' },
  pending_activation: { text: '待激活', color: '#1890FF' },
  expired: { text: '已过期', color: '#86909C' },
}

// 卡片类型映射
const CardTypeMap: Record<string, string> = {
  virtual_card: '虚拟卡',
  physical_card: '实体卡',
}

// 卡片信息接口
interface CardInfo {
  cardImage?: string
  cardNo?: string
  cardStatus?: string
  cardType?: string
}

export default function ConsumptionRecords() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const cardId = searchParams.get('cardId') || ''
  const { getTransactionStatus } = useTransactionStatus()

  // 从路由 state 获取卡片信息
  const cardInfo: CardInfo = location.state || {}

  const [data, setData] = useState<getUserCardConsumptionRecordListItemResp[]>([])
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 20

  const handleRecordClick = useCallback((record: getUserCardConsumptionRecordListItemResp) => {
    navigate('/card-v2/consumption-detail', { state: { record } })
  }, [navigate])

  const fetchRecords = useCallback(async (page: number, isAppend: boolean = false) => {
    if (loading || !cardId) return
    setLoading(true)
    try {
      const response = await APIWeb.getUserCardConsumptionRecordV2({
        card_id: Number(cardId),
        page,
        page_size: pageSize
      })
      if (response?.list) {
        const newData = response.list

        if (isAppend) {
          setData(prevData => [...prevData, ...newData])
        } else {
          setData(newData)
        }

        if (newData.length < pageSize) {
          setHasMore(false)
        } else {
          setCurrentPage(page + 1)
          setHasMore(true)
        }
      } else {
        setHasMore(false)
      }
    } catch (error) {
      console.error('获取消费记录失败:', error)
      setHasMore(false)
    } finally {
      setLoading(false)
    }
  }, [cardId, loading])

  useEffect(() => {
    if (cardId) {
      setData([])
      setCurrentPage(1)
      setHasMore(true)
      fetchRecords(1, false)
    }
  }, [cardId])

  const loadMore = useCallback(async () => {
    await fetchRecords(currentPage, true)
  }, [fetchRecords, currentPage])

  const onRefresh = useCallback(async () => {
    setCurrentPage(1)
    setHasMore(true)
    await fetchRecords(1, false)
  }, [fetchRecords])

  // 骨架屏
  const renderRecordSkeleton = useCallback(() => {
    return (
      <div className="flex items-center py-4 px-5">
        <SkeletonBox className="w-10 h-10 rounded-full flex-shrink-0" />
        <div className="flex-1 ml-3">
          <SkeletonBox className="w-24 h-4 rounded mb-2" />
          <SkeletonBox className="w-32 h-3 rounded" />
        </div>
        <div className="text-right">
          <SkeletonBox className="w-20 h-4 rounded mb-2" />
          <SkeletonBox className="w-12 h-3 rounded ml-auto" />
        </div>
      </div>
    )
  }, [])

  // 渲染单条记录
  const renderRecordItem = useCallback((record: getUserCardConsumptionRecordListItemResp) => {
    const icon = getTransactionIcon(record.txn_type)

    return (
      <Ripple className="rounded-xl">
        <div 
          className="flex items-center py-4 px-5"
          onClick={() => handleRecordClick(record)}
        >
          {/* 左侧图标 */}
          <div className="w-10 h-10 rounded-full bg-[#F2F4F7] flex items-center justify-center flex-shrink-0">
            <img src={icon} alt="" className="w-5 h-5" />
          </div>

          {/* 中间内容 */}
          <div className="flex-1 ml-3 min-w-0">
            <div className="text-sm font-medium text-[#1D2129] truncate">
              {getTransactionStatus(record.txn_type || '--', undefined, record.txn_type_string)}
            </div>
            <div className="text-xs text-[#86909C] mt-1">
              {formatDateTime(record.transaction_time)}
            </div>
          </div>

          {/* 右侧金额和状态 */}
          <div className="text-right flex-shrink-0 ml-3">
            <div className="text-sm font-semibold">
              {record.total_amt != null && record.total_amt >= 0 ? '+' : '-'}${formatThousands(Math.abs(record.total_amt || 0), 2, true)}
            </div>
            <div className="text-xs text-[#86909C] mt-1">
              {t('已完成')}
            </div>
          </div>
        </div>
      </Ripple>
    )
  }, [getTransactionStatus, handleRecordClick, t])

  // 获取状态显示
  const getStatusDisplay = useCallback((status?: string) => {
    const statusInfo = CardStatusMap[status || ''] || { text: '-', color: '#86909C' }
    return statusInfo
  }, [])

  // 获取卡片类型显示
  const getCardTypeDisplay = useCallback((type?: string) => {
    return CardTypeMap[type || ''] || '虚拟卡'
  }, [])

  const statusInfo = getStatusDisplay(cardInfo.cardStatus)

  return (
    <PageLayout>
      <Header
        leftChildren={<Back />}
        centerChildren={<span className="text-base font-medium">{t('交易记录')}</span>}
      />

      {/* 卡片信息头部 */}
      {cardInfo.cardNo && (
        <div className="bg-white px-4 py-3 flex items-center border-b border-[#EAECF0] border-b-solid">
          {/* 卡片图片 */}
          <div className="w-19.5 h-12.5 rounded-lg overflow-hidden flex-shrink-0 bg-gradient-to-br from-blue-400 to-blue-600">
            {cardInfo.cardImage ? (
              <img 
                src={cardInfo.cardImage} 
                alt="card" 
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <span className="text-white text-xs font-medium">ZISE</span>
              </div>
            )}
          </div>

          {/* 卡片信息 */}
          <div className="ml-4 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-[#1D2129]">
                {t(getCardTypeDisplay(cardInfo.cardType))}
              </span>
              <span className="text-sm text-[#1D2129]">
                **** {cardInfo.cardNo?.slice(-4) || '****'}
              </span>
            </div>
            <div className="flex items-center mt-1">
              <span 
                className="w-1.5 h-1.5 rounded-full mr-1.5"
                style={{ backgroundColor: statusInfo.color }}
              />
              <span className="text-xs" style={{ color: statusInfo.color }}>
                {t(statusInfo.text)}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* 记录列表 */}
      <div className="bg-white flex flex-col flex-1">
        <RefreshList
          data={data}
          renderItem={renderRecordItem}
          keyExtractor={(item, index) => `${item.id}-${index}`}
          loading={loading}
          hasMore={hasMore}
          onRefresh={onRefresh}
          onLoadMore={loadMore}
          renderSkeleton={renderRecordSkeleton}
          skeletonCount={8}
          emptyComponent={
            <EmptyState 
              text={t('暂无记录')}
              icon={<img src="/images/card-v2/card-no-records.svg" alt="" className="w-32 h-auto" />}
            />
          }
          finishedText={t('没有更多数据了')}
          loadingText={t('加载中')}
          className="flex-1"
        />
      </div>
    </PageLayout>
  )
}
